/**
 * Utility functions for Solar Power Health Diagnostics component
 */

/**
 * Generate mock solar panel health data
 */
export const generateMockHealthData = (config) => {
  const { chart, mockData } = config;
  const { gridRows, gridColumns } = chart.dimensions;
  const { probability } = mockData;
  
  const data = [];
  
  for (let y = 0; y < gridRows; y++) {
    for (let x = 0; x < gridColumns; x++) {
      const rand = Math.random();
      let status = 0; // Default to good
      
      if (rand > (1 - probability.critical)) {
        status = 2; // Critical
      } else if (rand > (1 - probability.critical - probability.needsAttention)) {
        status = 1; // Needs attention
      }
      
      data.push([x, y, status]);
    }
  }
  
  return data;
};

/**
 * Create ECharts option configuration
 */
export const createChartOption = (data, clusterName, config) => {
  const { chart, status } = config;
  const { colors, grid, dimensions, tooltip } = chart;
  
  // Create color map from status configuration
  const colorMap = {
    [status.good.value]: status.good.color,
    [status.needsAttention.value]: status.needsAttention.color,
    [status.critical.value]: status.critical.color
  };

  return {
    grid: grid,
    xAxis: {
      type: 'category',
      data: Array.from({ length: dimensions.gridColumns }, (_, i) => i + 1),
      splitLine: { show: false },
      axisLine: { lineStyle: { color: '#999' } },
      axisTick: { alignWithLabel: true }
    },
    yAxis: {
      type: 'category',
      data: Array.from({ length: dimensions.gridRows }, (_, i) => i + 1).reverse(),
      splitLine: { show: false },
      axisLine: { lineStyle: { color: '#999' } },
    },
    tooltip: tooltip.enabled ? {
      formatter: (params) => formatTooltip(params, clusterName, config, colorMap)
    } : {},
    visualMap: {
      show: false,
      dimension: 2,
      type: 'piecewise',
      pieces: [
        { value: status.good.value, color: status.good.color, label: status.good.label },
        { value: status.needsAttention.value, color: status.needsAttention.color, label: status.needsAttention.label },
        { value: status.critical.value, color: status.critical.color, label: status.critical.label }
      ],
    },
    series: [{
      type: 'heatmap',
      data,
      label: { show: false },
      itemStyle: {
        borderColor: colors.border,
        borderWidth: 1,
        color: (params) => colorMap[params.data[2]] || '#999'
      },
      emphasis: {
        itemStyle: {
          borderColor: colors.emphasizedBorder,
          borderWidth: 2
        }
      }
    }]
  };
};

/**
 * Format tooltip content
 */
const formatTooltip = (params, clusterName, config, colorMap) => {
  const { status, chart } = config;
  const { statusLabels } = chart.tooltip;
  const statusValue = params.data[2];
  const statusLabel = statusLabels[statusValue] || 'Unknown';
  const statusConfig = Object.values(status).find(s => s.value === statusValue);
  
  let details = '';
  const hasDetails = statusConfig?.details;
  if (hasDetails) {
    if (statusConfig.details.crackDetected) {
      details = statusConfig.details.customMessage || 'Crack detected';
    } else {
      const detailsArray = Object.entries(statusConfig.details)
        .filter(([key]) => key !== 'crackDetected' && key !== 'customMessage')
        .map(([key, value]) => {
          const formattedKey = key.replace(/([A-Z])/g, ' $1').toLowerCase();
          const capitalizedKey = formattedKey.charAt(0).toUpperCase() + formattedKey.slice(1);
          return `${capitalizedKey}: ${value}`;
        });
      details = detailsArray.join('<br/>');
    }
  }
  
  const panelX = params.data[0] + 1;
  const panelY = chart.dimensions.gridRows - params.data[1];
  
  return `${clusterName} - Panel (${panelX},${panelY})<br/>` +
         `<span style="color:${colorMap[statusValue]}">${statusLabel}</span><br/>` +
         `${details}`;
};

/**
 * Get legend items from configuration
 */
export const getLegendItems = (config) => {
  const { status } = config;
  return Object.values(status).map(statusItem => ({
    color: statusItem.color,
    label: statusItem.label,
    value: statusItem.value
  }));
};

/**
 * Format last updated timestamp
 */
export const formatLastUpdated = (timestamp) => {
  if (!timestamp) return 'Last updated 2 mins ago';
  
  const now = new Date();
  const updateTime = new Date(timestamp);
  const diffMs = now - updateTime;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins === 0) return 'Last updated now';
  if (diffMins === 1) return 'Last updated 1 min ago';
  if (diffMins < 60) return `Last updated ${diffMins} mins ago`;
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours === 1) return 'Last updated 1 hour ago';
  if (diffHours < 24) return `Last updated ${diffHours} hours ago`;
  
  return updateTime.toLocaleDateString();
};

/**
 * Validate configuration object
 */
export const validateConfig = (config) => {
  const requiredPaths = [
    'widget.title',
    'chart.dimensions.gridRows',
    'chart.dimensions.gridColumns',
    'status.good',
    'status.needsAttention',
    'status.critical'
  ];

  for (const path of requiredPaths) {
    if (!getNestedValue(config, path)) {
      console.warn(`Missing required config value: ${path}`);
      return false;
    }
  }
  return true;
};

/**
 * Get nested object value by dot notation path
 */
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Generate API response structure
 */
export const generateMockApiResponse = (config) => {
  const clusters = config.mockData.clusters.filter(cluster => cluster.enabled);
  const clustersData = {};
  
  clusters.forEach(cluster => {
    clustersData[cluster.id] = {
      name: cluster.name,
      data: generateMockHealthData(config),
      lastUpdated: new Date().toISOString(),
      totalPanels: config.chart.dimensions.gridRows * config.chart.dimensions.gridColumns
    };
  });
  
  return {
    success: true,
    data: clustersData,
    timestamp: new Date().toISOString(),
    metadata: {
      totalClusters: clusters.length,
      dataSource: 'mock'
    }
  };
};
